<template>
  <Dialog
    v-model:visible="visible"
    :style="{ width: '450px' }"
    header="Question Details"
    :modal="true"
  >
    <div class="field">
      <label for="question">Question</label>
      <Textarea
        id="question"
        v-model.trim="form.question"
        required="true"
        rows="3"
        cols="20"
        fluid
        :invalid="submitted && !form.question"
      />
      <small
        v-if="submitted && !form.question"
        class="p-invalid"
      >Question is required.</small>
    </div>
    <div class="field">
      <label for="answer">Answer</label>
      <Textarea
        id="answer"
        v-model.trim="form.answer"
        required="true"
        rows="3"
        cols="20"
        fluid
        :invalid="submitted && !form.answer"
      />
      <small
        v-if="submitted && !form.answer"
        class="p-invalid"
      >Answer is required.</small>
    </div>

    <div
      v-if="!isNewGroupMode && groups.length > 0"
      class="field"
    >
      <label for="group">Group</label>
      <Dropdown
        id="group"
        v-model="form.group_id"
        :options="groups"
        option-label="title"
        option-value="id"
        placeholder="Select a Group"
        fluid
        :invalid="submitted && !form.group_id"
      />
      <small
        v-if="submitted && !form.group_id"
        class="p-invalid"
      >Group is required.</small>
      <div class="mt-2">
        <a
          href="#"
          @click.prevent="isNewGroupMode = true"
        >Create new group</a>
      </div>
    </div>

    <div
      v-if="isNewGroupMode || groups.length === 0"
      class="field"
    >
      <label for="newGroupTitle">New Group Title</label>
      <InputText
        id="newGroupTitle"
        v-model.trim="newGroupTitle"
        required="true"
        fluid
        :invalid="submitted && !newGroupTitle"
      />
      <small
        v-if="submitted && !newGroupTitle"
        class="p-invalid"
      >Group title is required.</small>
      <div
        v-if="groups.length > 0"
        class="mt-2"
      >
        <a
          href="#"
          @click.prevent="isNewGroupMode = false"
        >Select existing group</a>
      </div>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        icon="pi pi-times"
        class="p-button-text"
        @click="visible = false"
      />
      <Button
        label="Save"
        icon="pi pi-check"
        class="p-button-text"
        @click="save"
      />
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'primevue/usetoast'

const props = defineProps({
  question: { type: Object, default: null }
})

const emit = defineEmits(['saved'])

const visible = defineModel('visible', { type: Boolean, default: false })

const store = useStore()
const toast = useToast()

const groups = computed(() => store.state.groups.items)

const form = ref({ question: '', answer: '', group_id: null })
const isNewGroupMode = ref(false)
const newGroupTitle = ref('')
const submitted = ref(false)

// Re-seed the form each time the dialog opens, from the question being edited
// (if any). Editing always uses the existing group; creating defaults to the
// new-group form only when the user has no groups yet.
watch(visible, open => {
  if (!open) return

  form.value = props.question
    ? { ...props.question }
    : { question: '', answer: '', group_id: null }
  newGroupTitle.value = ''
  isNewGroupMode.value = props.question?.id ? false : groups.value.length === 0
  submitted.value = false
})

async function save () {
  submitted.value = true

  const isQuestionValid = form.value.question.trim() && form.value.answer.trim()
  const isGroupValid = isNewGroupMode.value ? newGroupTitle.value.trim() : form.value.group_id
  if (!isQuestionValid || !isGroupValid) return

  try {
    let groupId = form.value.group_id

    // Create a new group first when the user opted to, or has none yet.
    if (isNewGroupMode.value || groups.value.length === 0) {
      const newGroup = await store.dispatch('groups/createGroup', { title: newGroupTitle.value.trim() })
      groupId = newGroup.id
    }

    const questionData = {
      question: form.value.question.trim(),
      answer: form.value.answer.trim(),
      group_id: groupId
    }

    if (form.value.id) {
      await store.dispatch('questions/updateQuestion', { id: form.value.id, questionData })
      toast.add({ severity: 'success', summary: 'Successful', detail: 'Question Updated', life: 3000 })
    } else {
      await store.dispatch('questions/createQuestion', questionData)
      toast.add({ severity: 'success', summary: 'Successful', detail: 'Question Created', life: 3000 })
    }

    visible.value = false
    emit('saved')
  } catch (err) {
    const action = form.value.id ? 'update' : 'create'
    const detail = err?.status === 409 ? err.message : `Failed to ${action} question`
    toast.add({ severity: 'error', summary: 'Error', detail, life: 3000 })
    console.error(`${action} error:`, err)
  }
}
</script>
